import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ReactComponent as Clear } from 'assets/icons/close.svg';
import debounce from 'lodash/debounce';
import { twMerge } from 'tailwind-merge';
import ButtonComponent from './button/buttonComponent';

interface SearchInputProps {
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  inputClassName?: string;
  testID?: string;
  disabled?: boolean;
  onFocus?: (e: string | null | undefined) => void;
  handleOnChange: (e: string) => void;
  handleClearCallback?: () => void;
  maxLength?: number;
}

const DEBOUNCE_TIME = 500;

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      placeholder,
      handleOnChange,
      handleClearCallback,
      onFocus,
      disabled,
      className,
      inputClassName,
      testID,
      maxLength,
      defaultValue,
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const [hasText, setHasText] = useState<boolean>(!!defaultValue);

    const onChangeRef = useRef(handleOnChange);
    useEffect(() => {
      onChangeRef.current = handleOnChange;
    }, [handleOnChange]);

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => innerRef.current!, []);

    const handleSearch = useMemo(
      () =>
        debounce((query: string) => {
          onChangeRef.current(query);
        }, DEBOUNCE_TIME),
      [],
    );

    useEffect(() => {
      return () => {
        handleSearch.cancel();
      };
    }, [handleSearch]);

    const clearInput = () => {
      handleSearch.cancel();
      onChangeRef.current('');
      setHasText(false);
      setTimeout(
        () => handleClearCallback && handleClearCallback(),
        DEBOUNCE_TIME,
      );
      if (innerRef.current) {
        innerRef.current.value = '';
      }
    };

    return (
      <div
        className={twMerge(
          'relative pr-7 flex flex-row grow-0 gap-2 justify-between bg-white text-base border input-border__gray rounded-md placeholder-gray-500 text-gray-700 focus-within:outline-none focus-within:border-blue-400 overflow-hidden',
          className,
        )}
      >
        <input
          ref={innerRef}
          defaultValue={defaultValue}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testID}
          maxLength={maxLength}
          className={twMerge(
            'placeholder-gray-500 grow p-3 text-dark-gray font-semibold text-sm outline-none border-0 focus:outline-none',
            'placeholder:italic placeholder:font-normal placeholder:text-med-gray-3',
            inputClassName,
          )}
          onFocus={(event) => onFocus && onFocus(event.target.value)}
          onChange={(event) => {
            const { value } = event.target;
            setHasText(!!value);
            handleSearch(value);
          }}
        />

        {hasText && (
          <ButtonComponent
            type="icon"
            testID="clear-input"
            onClick={clearInput}
            stroke="stroke-med-gray"
            Icon={Clear}
            className="absolute enabled:hover:bg-transparent enabled:active:bg-transparent p-0 w-auto cursor-pointer right-3 top-1/2 -translate-y-1/2"
          />
        )}
      </div>
    );
  },
);

export default SearchInput;
